import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, LayoutGrid, CalendarClock, ClipboardList, ArrowUpLeft, CalendarDays } from "lucide-react";
import { useScrollTo } from "@/lib/scroll";
import { schoolList, getSchool } from "@/data/clubs";

const sections = [
  { label: "החוגים", target: "clubs", Icon: LayoutGrid },
  { label: "שעות ועלויות", target: "schedule", Icon: CalendarClock },
  { label: "הרשמה", target: "register", Icon: ClipboardList },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { school } = useParams();
  const current = school || "maayanot";
  const currentSchool = getSchool(current);
  const scrollTo = useScrollTo();
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onLanding = location.pathname === `/${current}`;

  const goSection = (target) => {
    setOpen(false);
    if (!onLanding) {
      navigate(`/${current}`);
      setTimeout(() => scrollTo(target), 600);
    } else {
      scrollTo(target);
    }
  };

  const goClub = (id) => {
    setOpen(false);
    navigate(`/${current}/club/${id}`);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled || open ? "bg-paper/85 backdrop-blur-xl border-b border-ink/10" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-10 h-16 md:h-20 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="nav-logo">
          <img src="/logo.webp" alt="לוגו ספורט והעשרה" className="w-10 h-10 md:w-12 md:h-12 object-contain" />
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-head font-extrabold text-base md:text-lg tracking-tight">ספורט והעשרה</span>
            <span className="text-[11px] md:text-xs text-ink/50 font-medium tracking-wide">מעיינות · הדסים · צורים</span>
          </span>
        </Link>

        <div className="flex items-center gap-2 md:gap-3">
          <div className="flex items-center gap-1 bg-ink/5 rounded-full p-1" data-testid="school-switcher">
            {schoolList.map((s) => (
              <Link
                key={s.id}
                to={`/${s.id}`}
                className={`px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm font-head font-semibold transition-colors ${
                  current === s.id ? "bg-ink text-paper" : "text-ink/60 hover:text-ink"
                }`}
                data-testid={`school-tab-${s.id}`}
              >
                {s.name}
              </Link>
            ))}
          </div>

          {/* Dropdown menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setOpen((o) => !o)}
              className="flex items-center gap-1.5 font-head font-semibold text-sm border border-ink/15 bg-white px-4 py-2.5 rounded-full hover:border-ink transition-colors"
              data-testid="menu-toggle"
              aria-expanded={open}
            >
              תפריט
              <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>

            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute left-0 mt-3 w-72 origin-top-left rounded-2xl border border-ink/10 bg-white shadow-xl overflow-hidden"
                  data-testid="menu-panel"
                >
                  <div className="p-2">
                    <button
                      onClick={() => { setOpen(false); navigate("/calendar"); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-right font-semibold bg-accent/10 text-accent hover:bg-accent/15 transition-colors"
                      data-testid="menu-calendar"
                    >
                      <CalendarDays className="w-4 h-4" />
                      לוח שבועי — כל החוגים
                    </button>
                    <div className="px-3 pt-3 pb-1 text-xs font-semibold text-ink/40">
                      {currentSchool.label} · מעבר מהיר
                    </div>
                    {sections.map(({ label, target, Icon }) => (
                      <button
                        key={target}
                        onClick={() => goSection(target)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-right font-medium hover:bg-accent/5 hover:text-accent transition-colors"
                        data-testid={`menu-${target}`}
                      >
                        <Icon className="w-4 h-4 text-accent" />
                        {label}
                      </button>
                    ))}
                  </div>

                  <div className="border-t border-ink/10 p-2 max-h-64 overflow-y-auto">
                    <div className="px-3 pt-1 pb-1 text-xs font-semibold text-ink/40">מעבר לחוג</div>
                    {currentSchool.clubs.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => goClub(c.id)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl text-right font-medium hover:bg-ink/[0.04] transition-colors"
                        data-testid={`menu-club-${c.id}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                          {c.name}
                        </span>
                        <ArrowUpLeft className="w-4 h-4 text-ink/30" />
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <a
            href={currentSchool.regUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-head font-semibold text-xs md:text-sm bg-accent text-white px-3.5 md:px-5 py-2.5 rounded-full hover:bg-accent-600 transition-colors shrink-0 whitespace-nowrap"
            data-testid="nav-cta"
          >
            הרשמה באתר המועצה
          </a>
        </div>
      </div>
    </motion.header>
  );
};
